import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import { TeamAccess } from "target_wasm";
import { useShallow } from "zustand/react/shallow";
import { createLogger } from "@/debug/DebugingLogger";
import { pollingAllowed } from "@/components/hooks/activityGate";
import { useTeamsStore } from "@/components/main/stores/teamsStore";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import { useAccountScheduledForDeletion } from "@/components/hooks/useAccountScheduledForDeletion";
import { SearchBar } from "@/components/common/SearchBar";
import { MetaAddButton } from "@/components/main/datasets/DatasetMetaSection";
import { SectionRule } from "components/common/SectionRule";
import { ModelRow } from "@/components/main/overview/ModelRow";
import { formatCompactRelativeTime } from "@/components/main/overview/selectors";
import { sortModelsPinnedThenName } from "@/components/main/utils/models/types";
import { useCreateModel } from "@/components/main/utils/models/useCreateModel";

import { LIST_MAX_ROWS } from "@/components/main/pages/pageColumn";

const log = createLogger("TeamModels");

/// Model rows shown before the list scrolls; the header and the count + New
/// model footer stay fixed above/below it. Shared with the other list pages so
/// the team page's column matches theirs.
const MAX_VISIBLE_MODELS = LIST_MAX_ROWS;

interface TeamModelsProps {
  team: TeamAccess;
  isAdmin: boolean;
}

/// The team's models, rendered as the Overview `ModelRow` list. Row-click opens
/// the model (returning to this team page on close); the row context menu
/// carries the team action ("Remove from team" for admins) + team access.
export const TeamModels: React.FC<TeamModelsProps> = ({ team, isAdmin }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const isAccountScheduled = useAccountScheduledForDeletion();

  const { teamModels, fetchTeamModels, teamDetailsLoading } = useTeamsStore(
    useShallow((state) => ({
      teamModels: state.teamModels,
      fetchTeamModels: state.fetchTeamModels,
      teamDetailsLoading: state.teamDetailsLoading,
    })),
  );

  const teamId = useMemo(() => {
    try {
      return team.team_id_str();
    } catch (error) {
      log.error("Error extracting team ID", error);
      return "";
    }
  }, [team]);

  // Same create-model action + settle-in animation as the Overview page.
  const { freshModelId, isCreating, createModel } = useCreateModel({ teamId });

  const modelsForTeam = teamId ? teamModels[teamId] : undefined;
  const availableModels = useMemo(() => modelsForTeam || [], [modelsForTeam]);
  const modelsAreLoaded = modelsForTeam !== undefined;

  useEffect(() => setSearchQuery(""), [teamId]);

  const filteredModels = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const filtered = !query
      ? availableModels
      : availableModels.filter((model) =>
          (model.name || "Unnamed Model").toLowerCase().includes(query),
        );
    // Team list: pinned (starred) models on top, everyone else alphabetical.
    return sortModelsPinnedThenName(filtered);
  }, [availableModels, searchQuery]);

  const updateModelList = useCallback(
    async (refresh = false, showLoading = false) => {
      if (!teamId) return;
      try {
        await fetchTeamModels(teamId, refresh, showLoading);
      } catch (error) {
        log.error("Failed to update model list", error);
      }
    },
    [teamId, fetchTeamModels],
  );

  // Initial load + refresh on tab-focus/visibility, plus a TTL-aligned poll
  // (most ticks are no-ops). Mirrors the previous card-grid behavior.
  const hasInitiatedLoad = useRef<Record<string, boolean>>({});
  const isModelsLoading = teamId ? teamDetailsLoading[teamId] || false : false;
  useEffect(() => {
    if (!teamId) return;
    if (
      !modelsAreLoaded &&
      !isModelsLoading &&
      !hasInitiatedLoad.current[teamId]
    ) {
      hasInitiatedLoad.current[teamId] = true;
      void updateModelList(false, false);
    }
    const onVisibility = () => {
      if (!document.hidden && navigator.onLine) void updateModelList(true);
    };
    document.addEventListener("visibilitychange", onVisibility);
    const intervalId = window.setInterval(() => {
      if (!navigator.onLine || !pollingAllowed()) return;
      void updateModelList(true);
    }, 30_000);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
      delete hasInitiatedLoad.current[teamId];
    };
  }, [teamId, updateModelList, isModelsLoading, modelsAreLoaded]);

  const onDuplicateSession = (message: string): void =>
    showNotificationGlobal(message);

  const returnPath = teamId ? `/teams/${teamId}` : "/teams";
  const canAdd = !isCreating && !isAccountScheduled;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="shrink-0 mb-4">
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Filter models…"
        />
      </div>

      <SectionRule
        inset
        icon={<DashboardOutlinedIcon sx={{ fontSize: 14 }} />}
        label="Models"
      />

      {/* Only the rows scroll (capped at MAX_VISIBLE_MODELS); the header above
          and the count + New model footer below stay put. Each ModelRow is
          item-height tall. */}
      <div
        className="overflow-y-auto"
        style={{
          maxHeight: `calc(var(--item-height) * ${MAX_VISIBLE_MODELS})`,
        }}
      >
        {filteredModels.length > 0 ? (
          filteredModels.map((model) => (
            <ModelRow
              key={model.model_id}
              model={model}
              isFresh={String(model.model_id) === freshModelId}
              isTeamContext
              teamId={teamId}
              canDissociate={isAdmin}
              teamAccess={model.team_access}
              returnPath={returnPath}
              meta={
                model.last_opened_by_me != null
                  ? formatCompactRelativeTime(model.last_opened_by_me)
                  : undefined
              }
              onDuplicateSession={onDuplicateSession}
            />
          ))
        ) : availableModels.length > 0 ? (
          <div className="text-center py-12 text-small text-fg-muted italic">
            No models match “{searchQuery.trim()}”.
          </div>
        ) : (
          <div className="text-center py-12 text-small text-fg-muted italic">
            No models in this team yet.
          </div>
        )}
      </div>

      {/* Stays visible below the scrolling list. Flush right so the New model
          pill lines up with the full-width search bar above and the team
          header's flush description pill. */}
      <div className="flex items-center mt-6 shrink-0">
        <span
          className="font-mono text-fg-muted"
          style={{
            fontSize: "var(--font-size-x-small)",
            letterSpacing: "0.04em",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {`${availableModels.length} ${availableModels.length === 1 ? "model" : "models"}`}
        </span>
        {canAdd && (
          <span className="ml-auto">
            <MetaAddButton
              label="New model"
              onClick={() => void createModel()}
            />
          </span>
        )}
      </div>
    </div>
  );
};
